import { useEffect, useRef } from 'react';

// Store
import {
  useIsAllUsersFetched,
  useAppStore,
  useIsAuthorized,
  useIsLoggingOut,
} from '@/stores';

// Constants
import { REFRESH_INTERVAL } from '@/constants';

// Providers
import { useFirebase } from '@/providers/firebase';

/**
 * Auto-refresh users data every hour
 * - Fetch on mount if not already fetched
 * - Auto refresh every hour via interval (but only if all users not fetched)
 * - Cleans up interval on unmount
 * - Stops making API calls when user is not authorized or when force logout is in progress
 */
export const useGetUsers = () => {
  const isAllUsersFetched = useIsAllUsersFetched();
  const isAuthorized = useIsAuthorized();
  const isLoggingOut = useIsLoggingOut();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const { getToken, isLoading } = useFirebase();

  // Initial fetch if needed
  useEffect(() => {
    const fetchUsers = async () => {
      const token = await getToken();

      useAppStore
        .getState()
        .fetchUsers({ requestConfig: { token }, force: true });
    };

    // Only fetch if authorized, not logging out, and not loading
    if (!isLoading && isAuthorized && !isLoggingOut) {
      fetchUsers();
    }

    // Don't need to add "getToken" to the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized, isLoggingOut, isLoading]);

  // Set up interval for periodic refresh
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Only set up interval if user is authorized, not logging out, and all users are not fetched
    if (isAuthorized && !isLoggingOut && !isAllUsersFetched) {
      // Set up new interval
      intervalRef.current = setInterval(async () => {
        const token = await getToken();

        // Prevent fetching if already loading or not authorized
        const currentState = useAppStore.getState();

        if (!currentState.isLoading && isAuthorized && !isLoggingOut) {
          await currentState.fetchUsers({
            force: true,
            requestConfig: {
              token,
            },
          });
        }
      }, REFRESH_INTERVAL);
    }

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // Don't need to put "getToken" in the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized, isLoggingOut, isAllUsersFetched]);
};
